// Arpeely Attribution Script
// Captures rpclid + UTMs → cart attributes → order notes → backend S2S
(function () {
  // Session flag: prevents multiple syncs per browser session
  var SYNC_FLAG_KEY = 'arpeely_attrib_synced_v1';
  
  // Debug mode: set to true to see console logs
  var DEBUG = false;

  // Check if debug mode is enabled (local DEBUG var or window.ARPEELY_ATTRIBUTION_DEBUG)
  function isDebugEnabled() {
    try {
      if (typeof window === 'undefined') return false;
      if (DEBUG === true) return true;
      var v = window.ARPEELY_ATTRIBUTION_DEBUG;
      return v === true || v === '1' || v === 'true';
    } catch (e) {
      return false;
    }
  }

  // Log messages to console with [arpeely-attribution] prefix (only when debug enabled)
  function log() {
    if (!isDebugEnabled()) return;
    try {
      if (typeof console === 'undefined' || !console || !console.log) return;
      var args = Array.prototype.slice.call(arguments || []);
      args.unshift('[arpeely-attribution]');
      console.log.apply(console, args);
    } catch (e) {}
  }

  function getSearchParams() {
    try {
      return new URLSearchParams(window.location.search || '');
    } catch (e) {
      return new URLSearchParams('');
    }
  }

  function getCookie(name) {
    try {
      var cookies = (document.cookie || '').split(';');
      for (var i = 0; i < cookies.length; i++) {
        var c = cookies[i].trim();
        if (!c) continue;
        if (c.indexOf(name + '=') === 0) {
          return decodeURIComponent(c.substring(name.length + 1));
        }
      }
    } catch (e) {}
    return undefined;
  }

  // Collect Arpeely attribution data from:
  // - URL param: ?rpclid=...
  // - Arpeely cookies: __rpixel_rpclid, __rpixel_cid
  // - UTM params: ?utm_source=..., ?utm_medium=..., etc.
  // Returns: { rpclid: '...', utm_source: '...', utm_medium: '...', ... }
  function collectAttrib() {
    var p = getSearchParams();
    var out = {};

    // Arpeely uses rpclid - check URL first, then cookies
    var rpclid = p.get('rpclid');
    if (!rpclid) {
      rpclid = getCookie('__rpixel_rpclid') || getCookie('__rpixel_cid');
    }
    if (rpclid) {
      out.rpclid = rpclid;
      log('Collected rpclid', rpclid);
    }

    var utmKeys = ['source', 'medium', 'campaign', 'content', 'term'];
    for (var i = 0; i < utmKeys.length; i++) {
      var k = utmKeys[i];
      var v = p.get('utm_' + k);
      if (v) {
        out['utm_' + k] = v;
        log('Collected UTM', 'utm_' + k, v);
      }
    }

    return out;
  }

  function hasAnyAttrib(a) {
    return a && Object.keys(a).length > 0;
  }

  // Check if we've already synced in this browser session
  function alreadySynced() {
    try {
      return window.sessionStorage.getItem(SYNC_FLAG_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  // Mark that we've synced in this session
  function markSynced() {
    try {
      window.sessionStorage.setItem(SYNC_FLAG_KEY, '1');
    } catch (e) {}
  }

  // Sync attribution to cart via /cart/update.js (for PDP and normal flows)
  // • Reads existing cart attributes (preserves igId, etc.)
  // • Merges in Arpeely data
  // • Updates cart once per session
  function syncToCart(attrib) {
    if (!hasAnyAttrib(attrib)) return;
    if (alreadySynced()) return;

    log('Starting syncToCart with attrib', attrib);

    try {
      log('Fetching /cart.js to read existing attributes');
      fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'same-origin'
      })
        .then(function (res) {
          if (!res || !res.ok) return null;
          return res.json();
        })
        .then(function (cart) {
          if (!cart || typeof cart !== 'object') return;

          log('Loaded cart from /cart.js', cart);

          var existing = {};
          try {
            if (cart.attributes && typeof cart.attributes === 'object') {
              for (var k in cart.attributes) {
                if (!Object.prototype.hasOwnProperty.call(cart.attributes, k)) continue;
                existing[k] = cart.attributes[k];
              }
              log('Existing cart attributes', existing);
            }
          } catch (e) {}

          var merged = {};
          for (var kExisting in existing) {
            if (!Object.prototype.hasOwnProperty.call(existing, kExisting)) continue;
            merged[kExisting] = existing[kExisting];
          }
          for (var kAttrib in attrib) {
            if (!Object.prototype.hasOwnProperty.call(attrib, kAttrib)) continue;
            merged[kAttrib] = attrib[kAttrib];
          }

          log('Merged attributes to send to /cart/update.js', merged);

          var payload = { attributes: merged };

          try {
            fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify(payload),
              credentials: 'same-origin'
            })
              .then(function () {
                log('Successfully updated cart attributes via /cart/update.js');
                markSynced();
              })
              .catch(function () {
                log('Error calling /cart/update.js (caught in promise)');
              });
          } catch (e) {
            log('Error preparing /cart/update.js call', e && (e.message || e));
          }
        })
        .catch(function () {
          log('Error fetching /cart.js');
        });
    } catch (e) {
      log('Unexpected error in syncToCart', e && (e.message || e));
    }
  }

  // Rewrite cart permalink links to include attribution in URL (for direct-to-checkout flows)
  // • Finds links like /cart/VARIANT:QTY
  // • Appends ?attributes[rpclid]=...&attributes[utm_source]=...
  // • Preserves existing params (igId, discount codes, etc.)
  function rewriteCartPermalinks(attrib) {
    if (!hasAnyAttrib(attrib)) {
      log('No attribution to add to cart permalinks');
      return;
    }

    log('Starting rewriteCartPermalinks with attrib', attrib);

    try {
      var links = document.querySelectorAll('a[href*="/cart/"]');
      log('Found', links.length, 'potential cart permalink(s)');

      for (var i = 0; i < links.length; i++) {
        var link = links[i];
        var href = link.getAttribute('href');
        if (!href) continue;
        if (link.getAttribute('data-arpeely-rewritten') === '1') continue;

        try {
          var url;
          try {
            url = new URL(href, window.location.origin);
          } catch (e) {
            log('Failed to parse URL for link', href, e);
            continue;
          }

          if (!url.pathname.match(/\/cart\/\d+:\d+/)) {
            log('Skipping link (not a cart permalink pattern)', href);
            continue;
          }

          log('Processing cart permalink', href);

          for (var key in attrib) {
            if (!Object.prototype.hasOwnProperty.call(attrib, key)) continue;
            var value = attrib[key];
            if (!value) continue;

            var paramName = 'attributes[' + key + ']';
            url.searchParams.set(paramName, value);
            log('Added to permalink:', paramName, '=', value);
          }

          var newHref = url.toString();
          link.setAttribute('href', newHref);
          link.setAttribute('data-arpeely-rewritten', '1');
          log('Rewrote cart permalink from', href, 'to', newHref);

        } catch (e) {
          log('Error processing individual link', href, e && (e.message || e));
        }
      }

      log('Finished rewriting cart permalinks');
    } catch (e) {
      log('Error in rewriteCartPermalinks', e && (e.message || e));
    }
  }

  // Main entry point
  // • Collect attribution from URL/cookies
  // • Sync to cart via /cart/update.js
  // • Rewrite cart permalink links
  function main() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    log('Initializing attribution on page', window.location && window.location.href);

    var attrib = collectAttrib();
    log('Final collected attribution payload', attrib);
    
    syncToCart(attrib);
    rewriteCartPermalinks(attrib);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', main);
    } else {
      main();
    }
  }
})();
